import { Dayjs } from 'dayjs';
import { useEffect, useState } from 'react';

import { type DatePickerProps } from '../../../mui-wrapper-components/DatePicker/DatePicker';

export const useDateRangeLargePicker = (
  id: string,
  minDate: string,
  maxDate: string,
  {
    id: startDateId,
    value: startDateValue,
    onChange: startDateChange,
    onError: onStartDateErrorProp,
    helperText: startHelperText,
    minDate: startMinDate,
    maxDate: startMaxDate,
  }: DatePickerProps,
  {
    id: endDateId,
    value: endDateValue,
    onChange: onEndDateChange,
    onError: onEndDateErrorProp,
    helperText: endHelperText,
    minDate: endMinDate,
    maxDate: endMaxDate,
  }: DatePickerProps
) => {
  // the start and end date are only used for validation
  const [startDate, setStartDate] = useState<Dayjs | null>(startDateValue as Dayjs | null);
  const [endDate, setEndDate] = useState<Dayjs | null>(endDateValue as Dayjs | null);
  const [startError, setStartError] = useState('');
  const [endError, setEndError] = useState('');

  const errorToReadable = (error: string | null) => {
    if (!error) {
      return null;
    }

    // TODO: Art Kulakow - move the strings into the message catalog once there is storybook support added
    switch (error) {
      case 'endDateBeforStartDate':
        return 'End date cannot be before start date';
      case 'minDate':
        return `Minimum date is ${minDate}`;
      case 'maxDate':
        return `Maximum date is ${maxDate}`;
      case 'invalidDate':
        return 'Invalid format. Enter MM/DD/YYYY';
      default:
        return `Error: ${error}`;
    }
  };

  useEffect(() => {
    if (startDate && startDate?.isValid() && endDate && endDate?.isValid()) {
      if (startDate.isBefore(startMinDate)) {
        setStartError('minDate');
        onStartDateErrorProp && onStartDateErrorProp(startDateId as string, 'minDate');
        return;
      } else if (startDate.isAfter(startMaxDate)) {
        setStartError('maxDate');
        onStartDateErrorProp && onStartDateErrorProp(startDateId as string, 'maxDate');
        return;
      } else if (endDate.isBefore(endMinDate)) {
        setEndError('minDate');
        onEndDateErrorProp && onEndDateErrorProp(endDateId as string, 'minDate');
        return;
      } else if (endDate.isAfter(endMaxDate)) {
        setEndError('maxDate');
        onEndDateErrorProp && onEndDateErrorProp(endDateId as string, 'maxDate');
        return;
      }

      if (startDate.isAfter(endDate)) {
        setStartError('endDateBeforStartDate');
        setEndError('endDateBeforStartDate');
        onStartDateErrorProp &&
          onStartDateErrorProp(startDateId as string, 'endDateBeforStartDate');
        onEndDateErrorProp && onEndDateErrorProp(endDateId as string, 'endDateBeforStartDate');
      } else {
        setStartError('');
        setEndError('');
      }
    }
  }, [
    startDate,
    endDate,
    onStartDateErrorProp,
    onEndDateErrorProp,
    startDateId,
    endDateId,
    startError,
    endError,
    startMinDate,
    startMaxDate,
    endMinDate,
    endMaxDate,
  ]);

  const handleStartDateChange = (id: string, date: Dayjs | null) => {
    // Save the start date
    setStartDate(date);

    // Clear the error
    setStartError(''); // clear local error
    onStartDateErrorProp && onStartDateErrorProp(id, null); // clear parent error
    startDateChange && startDateChange(id, date); // call parent onChange
  };

  const handleEndDateChange = (id: string, date: Dayjs | null) => {
    // Save the end date
    setEndDate(date);

    // Clear the error
    setEndError(''); // clear local error
    onEndDateErrorProp && onEndDateErrorProp(id, null); // parent clear error
    onEndDateChange && onEndDateChange(id, date); // call parent onChange
  };

  const onStartDateError = (id: string, reason: string | null) => {
    setStartError(reason || '');
    onStartDateErrorProp && onStartDateErrorProp(id, reason);
    if (endError === 'endDateBeforStartDate') {
      setEndError('');
      onEndDateErrorProp && onEndDateErrorProp(endDateId as string, null);
    }
  };

  const onEndDateError = (id: string, reason: string | null) => {
    setEndError(reason || '');
    onEndDateErrorProp && onEndDateErrorProp(id, reason);
    if (startError === 'endDateBeforStartDate') {
      setStartError('');
      onStartDateErrorProp && onStartDateErrorProp(endDateId as string, null);
    }
  };

  return {
    handleStartDateChange,
    handleEndDateChange,
    onStartDateError,
    onEndDateError,
    startError: !!startError,
    endError: !!endError,
    startHelpText: errorToReadable(startError) || startHelperText,
    endHelperText: errorToReadable(endError) || endHelperText,
  };
};
